import { FC, ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import durationPlugin from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";

import { Dropdown, Table, TableProps, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';

// Shared UI Library Components
import {
  CustomIcon,
  CustomTooltip,
  EIconName,
  ListEllipsis,
  UserAvatar,
  Loader,
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './transactions-table.scss';

import { RecordingVideoCallModal } from '../../../../modals/recording-video-call-modal/recording-video-call-modal';
import { RefundModal } from '../../../../modals/refund-modal/refund-modal';

// Store
import { getRecordedCall, getTransactions, refundCompanyTransaction } from '../../../../store/transactions/transactions.thunks';
import { TransactionsSliceActions } from '../../../../store/transactions/transactions.slice';

// Models
import { ISorter, ITransactionItem, ITransactionSettings } from '../../../../interfaces/transactions.interfaces';
import { TTransactionType } from '../../../../types/transactions.types';
import { ISpecialization } from '../../../../interfaces/core.interfaces';

type TTransactionsTableProps = {
  transactionsList: ITransactionItem[];
  transactionsSettings: ITransactionSettings;
  total: number;
  loading?: boolean;
};

export const TransactionsTable: FC<TTransactionsTableProps> = (
  { transactionsList, transactionsSettings, total, loading = false }: TTransactionsTableProps
): JSX.Element => {

  dayjs.extend(durationPlugin);
  dayjs.extend(utc);

  const [page, setPage] = useState<number>(1);
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [openRecordingCallModal, setOpenRecordingCallModal] = useState<boolean>(false);
  const [activeRecordingCallLink, setActiveRecordingCallLink] = useState<string>('');

  // Refund modal state
  const [openRefundModal, setOpenRefundModal] = useState<boolean>(false);
  const [refundTransaction, setRefundTransaction] = useState<ITransactionItem | null>(null);
  const [refundLoading, setRefundLoading] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const getSortParams = ({ column, order, columnKey }: ISorter): {
    order_by: string,
    order_type: string
  } => {
    if (!column) return {
      order_by: '',
      order_type: '',
    };
    return {
      order_by: columnKey as string,
      order_type: order ? order.slice(0, -3) : '',  // 'ascend' | 'descend'
    };
  }

  const sortTableByField = (sorter: ISorter): void => {
    if (!Object.keys(sorter).length) return;
    const sortParams = getSortParams(sorter);
    const settings = { ...transactionsSettings, ...sortParams, offset: 0 };
    dispatch(TransactionsSliceActions.setSettings(settings));
    dispatch(getTransactions({ settings }));
  }

  const onChange: TableProps<any>['onChange'] = (
    pagination,
    filters,
    sorter: any,
    extra
  ) => {
    const currentPage = pagination.current ? pagination.current : 1;
    if (page !== currentPage) {
      setPage(currentPage);
      dispatch(getTransactions({
        settings: {
          ...transactionsSettings,
          offset: (currentPage - 1) * getPageSize(),
          limit: getPageSize(),
        }
      }));
      return;
    }
    setPage(1);
    sortTableByField(sorter);
  };

  const getConsultationIcon = (type: TTransactionType): ReactNode => {
    if (type === 'message') {
      return <CustomIcon name={ EIconName.Message } size={ 'size-md' }/>;
    }
    if (type === 'audio_call') {
      return <CustomIcon name={ EIconName.MicOn } size={ 'size-md' }/>;
    }
    if (type === 'conversation') {
      return <CustomIcon name={ EIconName.Chat } size={ 'size-md' }/>;
    }
    return <CustomIcon name={ EIconName.CameraOn } size={ 'size-md' }/>;
  }

  const getPageSize = (): number => {
    const windowHeight = window.innerHeight;
    return Math.floor((windowHeight - 220) / 80); // 80 - height of column, 220 - another block height & header of table
  }

  const toggleInfoModal = (): void => {
    setOpenInfo(!openInfo);
  }
  const openInfoModal = (transaction: ITransactionItem): void => {
    toggleInfoModal();
  }
  const downloadCallRecording = async (transaction_id: number): Promise<void> => {
    dispatch(getRecordedCall({transaction_id}))
    .then(async (res: any) => {
      if (res.payload?.archive_link) {
        // const link = document.createElement("a");
        // link.href = res.payload.archive_link;
        // link.target = "_blank";
        // link.rel = "noopener noreferrer";
        // document.body.appendChild(link);
        // link.click();
        // document.body.removeChild(link);

        setActiveRecordingCallLink(res.payload.archive_link);
        openRecordingModal();
      }
    });
  }

  const openRecordingModal = (): void => {
    setOpenRecordingCallModal(true);
  }

  const closeRecordingModal = (): void => {
    setActiveRecordingCallLink('');
    setOpenRecordingCallModal(false);
  }

  // Refund modal handlers
  const handleOpenRefundModal = (transaction: ITransactionItem): void => {
    setRefundTransaction(transaction);
    setOpenRefundModal(true);
  }

  const handleCloseRefundModal = (): void => {
    setOpenRefundModal(false);
    setRefundTransaction(null);
  }

  const handleRefundSubmit = async (reason: string): Promise<void> => {
    if (!refundTransaction) return;
    setRefundLoading(true);
    const result = await dispatch(refundCompanyTransaction({
      transaction_id: refundTransaction.id,
      reason,
    }));
    setRefundLoading(false);
    if (result.payload?.success) {
      handleCloseRefundModal();
    }
  }

  const disabledShowRecordingAction = (transaction: ITransactionItem): boolean => {
    // For transactions before the recording_till functionality appeared, recording is not available
    if (!dayjs(transaction.created_at).utc().isAfter(dayjs('2025-01-09').utc())) {
      return true;
    }
    if (!transaction.has_recording) return true;
    const currentData = dayjs().utc();
    const recordingTillDate = transaction.has_recording_till ? dayjs(transaction.has_recording_till).utc() : currentData;
    const diffRecordingDate = recordingTillDate.diff(currentData, 'seconds');
    if (diffRecordingDate <= 0) return true;
    return false;
  }

  const getDropdownItems = (transaction: ITransactionItem) => {
    const items: any[] = [];

    // Call Recording is only available for companies with recording feature enabled
    if (transaction.company_recording_enabled) {
      items.push({
        key: 'downloadCall',
        disabled: disabledShowRecordingAction(transaction),
        label: (
          <div className="refund-menu-item">
            <CustomIcon size={'size-lg'} name={EIconName.CameraOn} />
            {transaction.has_recording && transaction.has_recording_till
              ? 'Open Call Recording'
              : 'Call Recording is processing'}
          </div>),
        onClick: () => downloadCallRecording(transaction.id)
      });
    }

    // Refund is available for all companies unless already refunded
    if (transaction.status !== 'refunded') {
      items.push({
        key: 'refund',
        label: <div className="refund-menu-item">Refund</div>,
        onClick: () => handleOpenRefundModal(transaction)
      });
    }

    return items;
  }

  return (
    <div className={ `transactions-table` }>
      <Table
        loading={ loading }
        locale={ { emptyText: 'No data to display' } }
        dataSource={ transactionsList }
        rowKey="id"
        pagination={ total > getPageSize() ? {
          current: page,
          pageSize: getPageSize(),
          total,
          showSizeChanger: false,
          position: ['bottomRight']
        } : false }
        onChange={ onChange }>
        <Column
          className={ 'date' }
          title="Date"
          key={ 'created_at' }
          align={ 'center' }
          dataIndex={ 'created_at' }
          sorter={ true }
          width="10%"
          render={ (created_at) => <div
            className="table--body table--body-date">{ dayjs(created_at).format('DD MMM YYYY') }</div> }
        />
        <Column
          className={ 'client' }
          title="Client"
          key={ 'customer_name' }
          dataIndex={ 'user' }
          width="27%"
          sorter={ true }
          render={ (user) => <div className="table--body table--body-client">
            <div className="client-avatar">
              <UserAvatar avatarUrl={ user?.photo } sizeClass={ 'small' }/>
            </div>
            <div className="client-info">
              <div className="client-info--name">
                <CustomTooltip
                  title={ `${ user.first_name } ${ user.last_name }` }>{ user.first_name } { user.last_name }</CustomTooltip>
              </div>
              <div className="client-info--email">
                <a href={ `mailto:${ user.email }` }>{ user.email }</a>
              </div>
              {/*<div className="client-info--status">*/ }
              {/*  Not Registered*/ }
              {/*</div>*/ }
            </div>
          </div> }
        />
        { <Column
          title="Consultant"
          className={ 'counsellor' }
          key={ 'worker_name' }
          dataIndex={ 'worker' }
          sorter={ true }
          width="27%"
          render={ (worker) => <div className="table--body table--body-counsellor">
            <div className="counsellor-avatar">
              <UserAvatar avatarUrl={ worker?.photo } sizeClass={ 'small' }/>
            </div>
            <div className="counsellor-info">
              <div className="counsellor-info--name">
                <CustomTooltip title={ `${ worker.first_name } ${ worker.last_name }` }>
                  <Link to={ `/counsellors/${ worker.id }` }> { worker.first_name } { worker.last_name }</Link>
                </CustomTooltip>

              </div>
              <div className="counsellor-info--specialisations">
                <ListEllipsis items={ worker.specialities?.map((s: ISpecialization) => s.name) } hasCounter={ true }/>
              </div>
            </div>
          </div> }
        /> }
        <Column
          title="Service Details"
          className={ 'details' }
          align={ 'center' }
          key={ 'Service Details' }
          render={ ({ created_at, duration, total, type, free_duration }) => <div className="table--body table--body-details">
            <div className="details-info">
              { getConsultationIcon(type) }
              <b>{ dayjs(created_at).format('DD MMM YYYY') },</b> { dayjs(created_at).format('hh:mm A, ') }
              { duration?.text ? `${ duration?.text },` : '' }
              <b> { total?.text }</b>
              { free_duration && <b>
                |Free: { free_duration }
              </b> }
            </div>
          </div> }
        />
        <Column
          title="Amount"
          className={ 'amount' }
          key={ 'Amount' }
          align={ 'center' }
          width="10%"
          render={(transaction: ITransactionItem) => <div className={'amount-wrap'}>
            <div className={`table--body table--body-amount${transaction.status === 'refunded' ? ' amount--strikethrough' : ''}`}>
              {transaction.total?.text}
            </div>
            {transaction.status === 'refunded' ? (
              transaction.refund_reason ? (
                <Tooltip title={transaction.refund_reason}>
                  <div className="payment-status payment-status--refunded">Refunded</div>
                </Tooltip>
              ) : (
                <div className="payment-status payment-status--refunded">Refunded</div>
              )
            ) : (
              <div className="payment-status payment-status--succeeded">Succeeded</div>
            )}
          </div>}
        />
        <Column
          title="Actions"
          className={ 'actions' }
          key={ 'actions' }
          align={ 'center' }
          width="10%"
          render={(transaction) => {
            const items = getDropdownItems(transaction);
            return (
              <div className="actions--body">
                <div className="body--item actions">
                  {items.length > 0 && (
                    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight" arrow>
                      <CustomIcon name={EIconName.BurgerMenu} />
                    </Dropdown>
                  )}
                </div>
              </div>
            );
          }}
        />
      </Table>

      { openRecordingCallModal &&
        <RecordingVideoCallModal
          open={openRecordingCallModal}
          activeRecordingCallLink={activeRecordingCallLink}
          onCancel={closeRecordingModal} />}

      {openRefundModal && refundTransaction &&
        <RefundModal
          open={openRefundModal}
          amount={refundTransaction.total?.text || ''}
          loading={refundLoading}
          onSubmit={handleRefundSubmit}
          onCancel={handleCloseRefundModal} />}
    </div>
  );
};
